import { ForecastParameters } from "@domain/forecast/models/ForecastParameters";
import { ChevronRight, Refresh } from "@mui/icons-material";
import { Button, Card, Chip, CircularProgress, IconButton, Menu, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { Product } from "@domain/product/models/Product";
import { useRunForecast } from "../hooks/useForecast";
import { Forecast } from "@domain/forecast/models/Forecast";

interface UpdateForecastProps {
    product: Product;
    forecast: Forecast;
}

export default function UpdateForecast({ product, forecast }: UpdateForecastProps) {

    const theme = useTheme();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const [isRefreshing, setIsRefreshing] = useState(false);

    const { runProductForecast, forecastRuns, shouldUpdate } = useRunForecast(product.id);

    const handleRefreshForecast = async () => {
        setIsRefreshing(true);
        await runProductForecast({ productId: product.id });
        setIsRefreshing(false);
    };

    const compareForecastParameters = (
        forecastParameters: ForecastParameters,
        productParameters: ForecastParameters
    ) => {
        const allKeys = new Set([
            ...Object.keys(forecastParameters || {}),
            ...Object.keys(productParameters || {}),
        ]);
        const result: Record<string, { forecastValue: string; productValue: string }> = {};
        allKeys.forEach(key => {
            const forecastValue = forecastParameters?.[key as keyof ForecastParameters];
            const productValue = productParameters?.[key as keyof ForecastParameters];

            if (productValue !== undefined && forecastValue !== productValue) {
                result[key] = {
                    forecastValue: forecastValue !== undefined ? String(forecastValue) : "",
                    productValue: productValue !== undefined ? String(productValue) : "",
                };
            }
        });
        return result;
    };

    const waitTextVariants = [
        "Our AI is generating a forecast",
        "This can take up to a few minutes.",
        "Still working... Please be patient.",
        "Hang tight, your forecast is on its way."
    ];

    const [waitTextIndex, setWaitTextIndex] = useState(0);

    useMemo(() => {
        if (forecastRuns?.length === 0) {
            const interval = setInterval(() => {
                setWaitTextIndex((prev) => (prev + 1) % waitTextVariants.length);
            }, 10000);
            return () => clearInterval(interval);
        }
        // Reset index if no forecast running anymore
        setWaitTextIndex(0);
        return;
    }, [forecastRuns?.length]);

    const waitText = waitTextVariants[waitTextIndex];

    if (!shouldUpdate && (!product.forecastParameters || Object.keys(compareForecastParameters(forecast.parameters, product.forecastParameters ?? {})).length === 0)) {
        return null;
    }

    return (
        <Card sx={{
            p: 2,
            background: `linear-gradient(to bottom right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: theme.palette.secondary.contrastText,
        }}>
            {forecastRuns?.length && forecastRuns?.length > 0 || isRefreshing ? (
                <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" height={32}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="subtitle1">Running forecast...</Typography>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="subtitle2">{waitText}</Typography>

                        {forecastRuns?.length && forecastRuns?.length > 0 && (
                            <Chip color="secondary" label={`${forecastRuns?.[0]?.state_name}`} />
                        )}

                        <CircularProgress size={16} color="inherit" />
                    </Stack>
                </Stack>
            ) : (
                <Stack direction="row" spacing={2} alignItems="center" height={32}>
                    <Stack direction="row" spacing={0.5} alignItems="center" flex={1}>
                        <Typography variant="subtitle1">Apply updated forecast settings</Typography>

                        <IconButton size="small" color="inherit"
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
                        ><ChevronRight sx={{ fontSize: 20, transform: "rotate(90deg)" }} /></IconButton>
                    </Stack>

                    <Button
                        onClick={handleRefreshForecast}
                        endIcon={<Refresh />}
                        variant="contained" color="primary">
                        Refresh Forecast
                    </Button>

                    <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
                        <MenuItem disableRipple>
                            <Typography variant="subtitle2">
                                Forecast Parameters
                            </Typography>
                        </MenuItem>
                        <MenuItem disableRipple>
                            <Table size="small" sx={{ minWidth: 200, background: "transparent" }} aria-label="forecast parameter differences">

                                <TableBody>
                                    {Object.entries(compareForecastParameters(forecast.parameters, product.forecastParameters ?? {})).map(
                                        ([key, { forecastValue, productValue }]) => (
                                            <TableRow key={key}>
                                                <TableCell sx={{ color: "inherit", verticalAlign: "middle" }}>{key}:</TableCell>
                                                <TableCell sx={{ color: "inherit", verticalAlign: "middle" }}>{forecastValue}</TableCell>
                                                <TableCell sx={{ color: "inherit", verticalAlign: "middle" }}><ChevronRight sx={{ fontSize: 20 }} /></TableCell>
                                                <TableCell sx={{ color: "inherit", verticalAlign: "middle" }}>{productValue}</TableCell>
                                            </TableRow>
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </MenuItem>
                    </Menu>


                </Stack>
            )}
        </Card>

    );
}